import { makeAccess } from '../config/factory-controller'

/**
 * Gera conteúdo de impressão (HTML/ZPL) para a tela de Daily Sales reutilizando
 * o template de Journey (subtype=1). Envia os mesmos filtros usados na busca
 * de collection orders.
 *
 * mode: 'pdf' => type=0 (HTML)
 *       'zpl' => type=1 (ZPL)
 * subtype fixo: 1 (journey)
 */
export interface DailySalesPrintFilters {
  pageIndex: number
  pageSize: number
  status?: number
  startDate?: string
  endDate?: string
  token?: string
}

export async function fetchDailySalesReceiptContent(
  filters: DailySalesPrintFilters,
  mode: 'pdf' | 'zpl',
): Promise<string> {
  const API_URL =
    import.meta.env.VITE_REACT_APP_API_URL || 'https://localhost:50962'
  const cleanApiUrl = API_URL.replace(/\/+$/, '')

  const { key: applicationKey, token: applicationToken } = await makeAccess()
  if (!applicationKey || !applicationToken) {
    throw new Error('Credenciais de aplicação ausentes para impressão.')
  }
  const apiVersion = Number(import.meta.env.VITE_REACT_APP_API_VERSION)

  // Parâmetros de impressão
  const typeParam = mode === 'pdf' ? 0 : 1
  const subtype = 1 // journey template

  const endpoint = `${cleanApiUrl}/collectionorder/printcollectionorderlist`

  const bearer = localStorage.getItem('token')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-api-version': String(apiVersion),
    'x-application-key': applicationKey,
    'x-application-token': applicationToken,
  }
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`

  // Replicar exatamente os mesmos campos utilizados na listagem:
  // pageIndex, pageSize, status, initialDate, finalDate, token
  const rawBody: Record<string, any> = {
    pageIndex: filters.pageIndex,
    pageSize: filters.pageSize,
    // status só deve ir se definido (inclusive 0 é válido)
    ...(filters.status !== undefined ? { status: filters.status } : {}),
    ...(filters.startDate ? { initialDate: filters.startDate } : {}),
    ...(filters.endDate ? { finalDate: filters.endDate } : {}),
    ...(filters.token ? { token: filters.token } : {}),
  }

  // Limpeza defensiva (caso algo tenha ficado undefined)
  const body = Object.fromEntries(
    Object.entries(rawBody).filter(([, v]) => v !== undefined && v !== null),
  )

  const params = new URLSearchParams({
    type: String(typeParam),
    subtype: String(subtype),
  })

  const response = await fetch(`${endpoint}?${params.toString()}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    try {
      const errJson = await response.json()
      msg = errJson?.message || msg
    } catch {
      /* ignore */
    }
    throw new Error(msg)
  }

  const text = await response.text()
  try {
    const parsed = JSON.parse(text)
    if (typeof parsed === 'string') return parsed
    if (parsed?.data) {
      if (typeof parsed.data === 'string') return parsed.data
      if (parsed.data.content) return parsed.data.content
    }
    if (parsed.content) return parsed.content
    return JSON.stringify(parsed)
  } catch {
    return text
  }
}
